import React from 'react';
import { Droplet, Leaf, Recycle, Waves } from 'lucide-react';
import SectionLabel from '../ui-shree/SectionLabel';

/**
 * The Sanatana Economy — "Water is Life. We Feed it."
 * Editorial centerpiece used on Story + Transparency pages.
 * Four movements: opening vow, Kewra Circular Essence, prebiotic ritual,
 * and the closing pull-quote on conscious consumption.
 */
const WaterIsLife = ({ variant = 'full', testId = 'water-is-life' }) => (
  <section
    id="water-is-life"
    data-testid={testId}
    className="px-6 md:px-12 py-24 md:py-32 bg-[#7C9CB1] text-[#F4F8F9] relative overflow-hidden"
  >
    {/* Soft glow accent */}
    <span
      aria-hidden
      className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-30"
      style={{ background: 'radial-gradient(circle, #C1D7E2 0%, transparent 70%)' }}
    />

    <div className="relative max-w-[1300px] mx-auto">
      <SectionLabel testId={`${testId}-label`} className="opacity-80">
        The Sanatana Economy
      </SectionLabel>

      <h2
        data-testid={`${testId}-headline`}
        className="font-serif text-5xl md:text-7xl font-light leading-[1.02] mb-10 max-w-4xl"
      >
        Water is Life.
        <br />
        <em className="text-[#C1D7E2]">We Feed it.</em>
      </h2>

      <p
        data-testid={`${testId}-lede`}
        className="font-serif italic text-2xl md:text-3xl leading-relaxed text-[#F4F8F9]/95 max-w-3xl mb-16"
      >
        Our ritual does not end at the drain. We refuse harsh synthetics so
        that what you wash away becomes nourishment — not pollution — for the
        microbial ecosystem that keeps the world&rsquo;s waters clean.
      </p>

      {/* Three movements */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 mb-16">
        <Movement
          icon={Droplet}
          eyebrow="Circular Essence"
          title="Kewra, captured from sacrificed steam."
          body="Our base is not water. It is Kewra hydrosol — drawn from the very steam released when Pandanus blossoms are distilled. The plant gives its breath; we honour the full cycle, ignoring nothing."
          testId={`${testId}-kewra`}
        />
        <Movement
          icon={Leaf}
          eyebrow="Prebiotics for the Water"
          title="100 % biodegradable, by vow."
          body="Every oil, every botanical, every emulsifier in our formula is chosen so the molecule that leaves your skin is food for the wastewater microbiome. We feed the bacteria that feed the rivers."
          testId={`${testId}-biodegrade`}
        />
        <Movement
          icon={Waves}
          eyebrow="Sewer-Positive Impact"
          title="Downstream is sacred."
          body="Restoration plants, septic systems, monsoon drains — these are not afterthoughts. They are temples of cleansing. Shreesavaa is engineered to leave them stronger than it found them."
          testId={`${testId}-sewer`}
        />
      </div>

      {/* Closing vow on conscious consumption */}
      {variant === 'full' && (
        <div
          data-testid={`${testId}-vow`}
          className="border-t border-[#F4F8F9]/30 pt-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-start"
        >
          <div className="md:col-span-2">
            <Recycle size={32} strokeWidth={1.1} className="text-[#C1D7E2]" />
          </div>
          <div className="md:col-span-10">
            <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/70 mb-4">
              Conscious Consumption
            </p>
            <p className="font-serif text-2xl md:text-3xl leading-snug text-[#F4F8F9] max-w-3xl">
              We do not claim absence. We claim honesty. The pouch is
              compostable, the formula is biodegradable, the cycle is
              real — and a physical transition still happens at every
              stage. In the Sanatana economy, what one creature releases
              is another creature&rsquo;s feast. We simply make sure our
              offering is worthy of the next mouth in the cycle.
            </p>
          </div>
        </div>
      )}
    </div>
  </section>
);

const Movement = ({ icon: Icon, eyebrow, title, body, testId }) => (
  <article data-testid={testId} className="space-y-4">
    <Icon size={26} strokeWidth={1.1} className="text-[#C1D7E2]" />
    <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/75">
      {eyebrow}
    </p>
    <h3 className="font-serif text-2xl md:text-3xl leading-snug text-[#F4F8F9]">
      {title}
    </h3>
    <p className="font-light leading-relaxed text-[#F4F8F9]/85">{body}</p>
  </article>
);

export default WaterIsLife;
